import { z } from 'zod';

export const learnerDocumentContextSchema = z.object({
  kind: z.literal('registration'),
  id: z.string().min(1),
});

export const medicalQuestionnaireDocumentSchema = z.object({
  id: z.string().min(1),
  document_type: z.literal('medical_questionnaire'),
  state: z.enum(['draft', 'in_review', 'approved', 'not_valid']),
  context: learnerDocumentContextSchema,
  revision: z.number().int().positive(),
  display: z.object({ label: z.string().min(1) }),
  permitted_actions: z.array(z.enum(['provision', 'read', 'update_responses', 'submit', 'review'])),
  content: z.object({
    type: z.literal('medical_questionnaire'),
    responses: z.array(
      z.object({
        response_id: z.string().min(1),
        label: z.string().min(1),
        value: z.boolean().nullable(),
      })
    ),
    participant_signed_at: z.iso.datetime().nullable(),
    physician: z.object({
      name: z.string().nullable(),
      signed_at: z.iso.datetime().nullable(),
      information: z.string().nullable(),
    }),
    approval: z.boolean().nullable(),
  }),
});

export const learnerDocumentResponseSchema = z.object({ data: medicalQuestionnaireDocumentSchema });

export const courseCompletionFormDocumentSchema = z.object({
  id: z.uuid(),
  document_type: z.literal('course_completion_form'),
  state: z.enum(['unsigned', 'signed', 'unavailable']),
  context: learnerDocumentContextSchema,
  revision: z.number().int().positive(),
  display: z.object({ label: z.string().min(1) }),
  permitted_actions: z
    .array(z.enum(['read', 'sign', 'update_responses']))
    .refine((actions) => actions[0] === 'read' && new Set(actions).size === actions.length),
  content: z.object({
    type: z.literal('course_completion_form'),
    instructor_signed: z.boolean().nullable(),
    instructor: z.object({ name: z.string().nullable(), email: z.string().nullable() }).nullable(),
    center: z.object({ name: z.string().nullable() }).nullable(),
    course_start_date: z.iso.datetime().nullable(),
    certification: z.object({ id: z.uuid(), name: z.string().nullable() }).nullable(),
    instructor_signed_at: z.iso.datetime().nullable(),
    requirements: z.array(
      z.object({
        id: z.uuid(),
        category: z.string().nullable(),
        type: z.string().nullable(),
        required: z.boolean().nullable(),
        completed: z.boolean().nullable(),
        result: z.string().nullable(),
      })
    ),
  }),
});

export const learnerDocumentReadResponseSchema = z.object({
  data: z.discriminatedUnion('document_type', [
    medicalQuestionnaireDocumentSchema,
    courseCompletionFormDocumentSchema,
  ]),
});
export type CourseCompletionFormDocument = z.infer<typeof courseCompletionFormDocumentSchema>;

export const learnerDocumentMutationReceiptSchema = z.object({
  data: z.object({
    operationId: z.uuid(),
    documentId: z.uuid(),
  }),
});

export type LearnerDocumentContext = z.infer<typeof learnerDocumentContextSchema>;
export type MedicalQuestionnaireDocument = z.infer<typeof medicalQuestionnaireDocumentSchema>;
